import type { TestSelection } from '@/types/coaching'

export interface HandheldSelection {
  /** Opt in to the new page without changing legacy mobile destinations. */
  routeBase?: '/admin/coaching-new'
  /** Preserve only the new page's operations selection while opening grading. */
  contextParams?: URLSearchParams
  timeslotId?: number | null
  classFilter?: number | null
  learnerId?: number | null
  lessonId?: number | null
  attemptId?: number | null
  unitAssessmentId?: number | null
  test?: TestSelection | null
  /** 컴포넌트 상세 화면에서 열 채점 항목. 없으면 목록 화면이다. */
  section?: string | null
  /** 목록 보기 방식. 기본(클래스별)은 주소에 남기지 않는다. */
  layout?: HandheldListLayout | null
}

export type HandheldListLayout = 'classes' | 'learners'
export function handheldListLayout(value: string | null | undefined): HandheldListLayout {
  return value === 'learners' ? 'learners' : 'classes'
}

export function positiveSelectionId(value: string | null | undefined): number | null {
  if (!value || !/^\d+$/.test(value)) return null
  const id = Number(value)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

export function handheldCoachingHref({ routeBase, contextParams, timeslotId, classFilter, learnerId, lessonId, attemptId, unitAssessmentId, test, section, layout }: HandheldSelection): string {
  const params = new URLSearchParams()
  if (routeBase && contextParams) {
    for (const key of ['contentKind', 'contentId', 'contentTestKind', 'contentScopeId', 'contentChapter', 'contentAssessmentId', 'contentForce', 'contentCurriculum', 'contentCourse', 'contentModule']) {
      const value = contextParams.get(key)
      if (value != null) params.set(key, value)
    }
  }
  if (timeslotId != null) params.set('timeslotId', String(timeslotId))
  if (routeBase && classFilter != null) params.set('classFilter', String(classFilter))
  if (routeBase && learnerId != null) params.set('learnerId', String(learnerId))
  if (learnerId != null && unitAssessmentId != null && Number.isSafeInteger(unitAssessmentId) && unitAssessmentId > 0) {
    params.set('unitAssessmentId', String(unitAssessmentId))
  } else if (learnerId != null && test != null) {
    params.set('testKind', test.kind)
    params.set('chapterOrdinal', String(test.chapterOrdinal))
    params.set('scopeContentId', String(test.scopeContentId))
  } else if (learnerId != null && lessonId != null) {
    params.set('lessonId', String(lessonId))
    params.set('attempt', attemptId == null ? 'progress' : String(attemptId))
    if (section) params.set('section', section)
  }
  if (layout === 'learners') params.set('layout', layout)
  if (routeBase) params.set('workspace', 'grading')
  const path = routeBase ?? `/admin/m/coaching${learnerId == null ? '' : `/${learnerId}`}`
  return `${path}${params.size ? `?${params}` : ''}`
}

/** An allowlist, not an arbitrary post-login redirect. */
export function handheldLoginReturn(value: string | null): string | null {
  if (!value || Array.from(value).some((character) => character === '\\' || character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return null
  if (!/^\/admin\/(?:coaching-new|coaching-alt|m\/coaching(?:\/[1-9]\d*)?)(?:\?[^#]*)?$/.test(value)) return null
  return value
}

export const COACHING_EXIT_EVENT = 'coaching:request-exit'

/** Defer auth-changing actions until the mounted coaching page approves leaving. */
export function requestCoachingExit(action: () => void): void {
  if (window.dispatchEvent(new CustomEvent(COACHING_EXIT_EVENT, { detail: action, cancelable: true }))) action()
}
